import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Phone, MessageCircle, MapPin, ArrowRight, Wrench, Gauge, AlertTriangle, Car, PaintBucket, Tag } from "lucide-react";
import { seoHead, localBusinessSchema, complianceToBusinessOpts } from "@/lib/seo";
import { getPublicConfig } from "@/lib/site-content.functions";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { getBusinessConfig } from "@/lib/site-config";
import { GUIDES } from "@/lib/guides";

const HUB_FAQS = [
  { q: "Is this advice free?", a: `Yes — every guide here is free. If you'd rather speak to a mechanic directly, call us on ${NAP.phone} and we'll point you in the right direction with no obligation.` },
  { q: "Where is Woodseer Autos based?", a: "We're an independent DVSA-authorised garage at 16 Pixley Street, Poplar E14 7DF — workshop entrance is the yard at 14 Copenhagen Place. We serve drivers across Canary Wharf, Isle of Dogs, Limehouse, Whitechapel, Bow, Mile End and the wider Tower Hamlets area." },
  { q: "Do you offer free inspections?", a: "We offer a free 5-minute visual check for tyres, lights and obvious leaks. For diagnostic scans or a brake/suspension inspection on a lift we charge £45, and that fee comes off any repair we carry out." },
  { q: "Can you advise without me bringing the car in?", a: "Often yes. Send a photo or short video to our WhatsApp and we'll tell you whether it's worth a workshop visit." },
];

const CATEGORIES: { title: string; icon: React.ReactNode; desc: string; guides: string[] }[] = [
  {
    title: "MOT preparation",
    icon: <Gauge className="h-5 w-5" />,
    desc: "Pass first time. What testers look for and what to fix before the test.",
    guides: ["mot-warning-lights-before-test", "tyre-check-before-mot"],
  },
  {
    title: "Servicing & maintenance",
    icon: <Wrench className="h-5 w-5" />,
    desc: "What a real service should include and which intervals actually matter.",
    guides: ["selling-car-repair-checklist"],
  },
  {
    title: "Warning lights & diagnostics",
    icon: <AlertTriangle className="h-5 w-5" />,
    desc: "Engine light, ABS, airbag — what each one means and what to do next.",
    guides: ["engine-light-east-london", "mot-warning-lights-before-test"],
  },
  {
    title: "Brakes, tyres & suspension",
    icon: <Car className="h-5 w-5" />,
    desc: "Noises, pulling, uneven wear — diagnose before it becomes expensive.",
    guides: ["brake-noise-squeaking-grinding", "tyre-check-before-mot", "car-pulling-to-one-side"],
  },
  {
    title: "Bodywork, scratches & accident damage",
    icon: <PaintBucket className="h-5 w-5" />,
    desc: "What's worth repairing, what to claim on insurance, and what to leave.",
    guides: ["selling-car-repair-checklist"],
  },
  {
    title: "Buying & selling a used car",
    icon: <Tag className="h-5 w-5" />,
    desc: "Pre-purchase checks, asking-price boosters and what not to bother fixing.",
    guides: ["selling-car-repair-checklist"],
  },
];

export const Route = createFileRoute("/motorist-help")({
  loader: async () => {
    const cfg = await getPublicConfig().catch(() => null);
    return { compliance: cfg?.compliance || null };
  },
  head: ({ loaderData }) =>
    seoHead({
      path: "/motorist-help",
      title: "Free MOT & Car Care Advice for East London Drivers | Woodseer Autos",
      description:
        "Helpful MOT, servicing, warning light, brake, tyre and car repair advice from Woodseer Autos, an independent garage serving E14, Poplar, Canary Wharf and East London.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Motorist Help", path: "/motorist-help" },
      ],
      jsonLd: [
        localBusinessSchema(complianceToBusinessOpts(loaderData?.compliance)),
        faqSchema(HUB_FAQS) as Record<string, unknown>,
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Motorist Help Hub",
          url: "https://woodseerautos.co.uk/motorist-help",
          about: "Free car care, MOT and repair advice for East London drivers",
          isPartOf: { "@id": "https://woodseerautos.co.uk/#business" },
        },
      ],
    }),
  component: MotoristHelpPage,
});

function MotoristHelpPage() {
  const cfg = getBusinessConfig();
  return (
    <>
      <section className="py-14 md:py-20 bg-secondary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <p className="text-[11px] font-bold tracking-[0.22em] uppercase text-primary">Motorist Help Hub</p>
          <h1 className="mt-3 text-4xl md:text-5xl font-extrabold text-foreground tracking-tight">
            Free Car Care & MOT Advice for East London Drivers
          </h1>
          <p className="mt-4 text-base md:text-lg text-muted-foreground leading-relaxed">
            Practical, jargon-free guidance from an independent garage in Poplar, E14. Honest answers about MOTs, warning lights, brakes, tyres, diagnostics and bodywork — without the upsell.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a href={cfg.phoneHref} className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground">
              <Phone className="h-4 w-4" />Call {cfg.phone}
            </a>
            <a href={cfg.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground">
              <MessageCircle className="h-4 w-4" />WhatsApp
            </a>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-5xl px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {CATEGORIES.map((c) => (
              <div key={c.title} className="rounded-2xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-primary">{c.icon}<h2 className="text-base font-bold text-foreground">{c.title}</h2></div>
                <p className="mt-2 text-sm text-muted-foreground">{c.desc}</p>
                <ul className="mt-3 space-y-1.5">
                  {Array.from(new Set(c.guides)).map((slug) => {
                    const g = GUIDES.find((x) => x.slug === slug);
                    if (!g) return null;
                    return (
                      <li key={slug}>
                        <Link to="/guide/$slug" params={{ slug }} className="text-sm font-semibold text-foreground hover:text-primary inline-flex items-center gap-1">
                          {g.title}<ArrowRight className="h-3 w-3" />
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-secondary">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground">Unsure what your car needs?</h2>
          <p className="mt-3 text-sm text-muted-foreground">
            Call us with the symptom — squeak, light, smell, vibration — and we'll tell you straight whether it's a workshop job, a watch-and-wait, or something to fix yourself.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-2">
            <a href={cfg.phoneHref} className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground"><Phone className="h-4 w-4" />Call {cfg.phone}</a>
            <a href={cfg.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground"><MessageCircle className="h-4 w-4" />WhatsApp us</a>
            <Link to="/book" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground">Request booking<ArrowRight className="h-4 w-4" /></Link>
            <a href={cfg.googleMapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-3 text-sm font-bold text-foreground"><MapPin className="h-4 w-4" />Directions</a>
          </div>
        </div>
      </section>

      <FAQAccordion faqs={HUB_FAQS} title="Hub FAQs" />
    </>
  );
}
